import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import LandingPage from "@/pages/LandingPage";
import AttorneysPage from "@/pages/AttorneysPage";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import CaseWorkspace from "@/pages/CaseWorkspace";
import SharedCasePage from "@/pages/SharedCasePage";
import BeneficiaryReviewPage from "@/pages/BeneficiaryReviewPage";
import AdminHome from "@/pages/AdminHome";
import AdminRegisterPage from "@/pages/AdminRegisterPage";
import { TermsOfService, PrivacyPolicy } from "@/pages/LegalPages";
import SecurityPrivacyPage from "@/pages/SecurityPrivacyPage";
import AdminPricing from "@/pages/AdminPricing";
import AdminTesters from "@/pages/AdminTesters";
import AdminDirectory from "@/pages/AdminDirectory";
import AdminTestimonials from "@/pages/AdminTestimonials";
import AdminAffiliates from "@/pages/AdminAffiliates";
import AffiliateApplyPage from "@/pages/AffiliateApplyPage";
import AffiliateStatsPage from "@/pages/AffiliateStatsPage";
import AffiliateDashboardPage from "@/pages/AffiliateDashboardPage";
import ReferPage from "@/pages/ReferPage";
import AdminFriendsAndFamily from "@/pages/AdminFriendsAndFamily";
import DirectoryPage from "@/pages/DirectoryPage";
import DirectoryApplyPage from "@/pages/DirectoryApplyPage";
import PressLandingPage from "@/pages/PressLandingPage";
import PressKitPage from "@/pages/PressKitPage";
import AttorneyDashboard from "@/pages/AttorneyDashboard";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import AccountSecurityPage from "@/pages/AccountSecurityPage";
import VisaBulletinCheckerPage from "@/pages/VisaBulletinCheckerPage";
import WidgetPage from "@/pages/WidgetPage";
import SamplePacketPage from "@/pages/SamplePacketPage";
import FAQPage from "@/pages/FAQPage";
import PricingPage from "@/pages/PricingPage";
import CookieBanner from "@/components/CookieBanner";
import { PaymentCheckoutPage, PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentPages";
import InstallPrompt from "@/components/InstallPrompt";
import ReferralPrompt from "@/components/ReferralPrompt";
import "@/App.css";

/** Role-based home path: attorneys → /attorney, affiliates → /affiliate/dashboard,
 * everyone else → /dashboard. */
function roleHomePath(role) {
  if (role === "attorney") return "/attorney";
  if (role === "affiliate") return "/affiliate/dashboard";
  return "/dashboard";
}

/** Returns a redirect path when `user` shouldn't be on `path`, else null. */
function roleRedirectFor(user, path) {
  const isAttorneyRoute = path.startsWith("/attorney");
  const isClientRoute = path.startsWith("/dashboard") || path.startsWith("/case/");
  const isAffiliateOwnRoute = path.startsWith("/affiliate/dashboard");
  if (user.role === "attorney" && isClientRoute) return "/attorney";
  if (user.role === "affiliate" && (isClientRoute || isAttorneyRoute)) return "/affiliate/dashboard";
  if (user.role !== "attorney" && isAttorneyRoute) return roleHomePath(user.role);
  if (user.role !== "affiliate" && isAffiliateOwnRoute) return roleHomePath(user.role);
  return null;
}

function Protected({ children, role }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  const redirect = roleRedirectFor(user, location.pathname || "");
  if (redirect) return <Navigate to={redirect} replace />;
  if (role && user.role !== role) return <Navigate to="/dashboard" replace />;
  return children;
}

function DisclaimerFooter() {
  return (
    <footer className="disclaimer-band" data-testid="legal-disclaimer">
      DS-260USA.com is <strong>not a law firm</strong> and does not provide legal advice.
      By using this service you agree to our
      <Link to="/legal/terms" data-testid="footer-tos-link">Terms of Service</Link>
      and
      <Link to="/legal/privacy" data-testid="footer-privacy-link">Privacy Policy</Link>.
      See how we protect your data:
      <Link to="/security" data-testid="footer-security-link">Security &amp; Privacy</Link>.
      Always review your DS-260 answers before submitting them at ceac.state.gov.
    </footer>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <CookieBanner />
        <Routes>
          {/* Public marketing */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/attorneys" element={<AttorneysPage />} />
          {/* Localized landing pages: /es /hi /bn /pa /gu /ta /te /zh /tl /vi /ko */}
          <Route path="/es" element={<LandingPage locale="es" />} />
          <Route path="/hi" element={<LandingPage locale="hi" />} />
          <Route path="/bn" element={<LandingPage locale="bn" />} />
          <Route path="/pa" element={<LandingPage locale="pa" />} />
          <Route path="/gu" element={<LandingPage locale="gu" />} />
          <Route path="/ta" element={<LandingPage locale="ta" />} />
          <Route path="/te" element={<LandingPage locale="te" />} />
          <Route path="/zh" element={<LandingPage locale="zh" />} />
          <Route path="/tl" element={<LandingPage locale="tl" />} />
          <Route path="/vi" element={<LandingPage locale="vi" />} />
          <Route path="/ko" element={<LandingPage locale="ko" />} />
          <Route path="/directory" element={<DirectoryPage />} />
          <Route path="/directory/apply" element={<DirectoryApplyPage />} />
          <Route path="/press/:lang" element={<PressLandingPage />} />
          <Route path="/press-kit" element={<PressKitPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/login" element={<AuthPage mode="login" />} />
          <Route path="/register" element={<AuthPage mode="register" />} />
          <Route path="/legal/terms" element={<TermsOfService />} />
          <Route path="/legal/privacy" element={<PrivacyPolicy />} />
          <Route path="/security" element={<SecurityPrivacyPage />} />

          {/* Payments (must exist as top-level routes for Stripe redirects) */}
          <Route path="/payment/success" element={<Protected><PaymentSuccessPage /></Protected>} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />

          {/* Authenticated app */}
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/account" element={<Protected><AccountSecurityPage /></Protected>} />
          <Route path="/case/:id" element={<Protected><CaseWorkspace /></Protected>} />
          <Route path="/shared/:token" element={<SharedCasePage />} />
          <Route path="/beneficiary-review/:token" element={<BeneficiaryReviewPage />} />
          <Route path="/visa-bulletin-checker" element={<VisaBulletinCheckerPage />} />
          <Route path="/widget" element={<WidgetPage />} />
          <Route path="/sample-packet" element={<SamplePacketPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/attorney" element={<Protected><AttorneyDashboard /></Protected>} />
          <Route path="/case/:id/pay" element={<Protected><PaymentCheckoutPage /></Protected>} />
          <Route path="/admin" element={<Protected><AdminHome /></Protected>} />
          <Route path="/register-admin" element={<AdminRegisterPage />} />
          <Route path="/admin/pricing" element={<Protected><AdminPricing /></Protected>} />
          <Route path="/admin/testers" element={<Protected><AdminTesters /></Protected>} />
          <Route path="/admin/directory" element={<Protected><AdminDirectory /></Protected>} />
          <Route path="/admin/testimonials" element={<Protected><AdminTestimonials /></Protected>} />
          <Route path="/admin/affiliates" element={<Protected><AdminAffiliates /></Protected>} />
          <Route path="/affiliate" element={<AffiliateApplyPage />} />
          <Route path="/affiliate/stats" element={<AffiliateStatsPage />} />
          <Route path="/affiliate/dashboard" element={<Protected><AffiliateDashboardPage /></Protected>} />
          <Route path="/refer" element={<ReferPage />} />
          <Route path="/admin/friends-and-family" element={<Protected><AdminFriendsAndFamily /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <DisclaimerFooter />
        <InstallPrompt />
        <ReferralPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}
